import { useCallback, useEffect } from "react";
import * as React from "react";

import { useAlert } from "@hl/base-components/lib/alert/useAlert";
import TextAreaWithCounter from "@hl/base-components/lib/inputs/TextAreaWithCounter";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { GetUserAccountSettingsDocument } from "@hl/shared-features/lib/features/auth/queries/queries/accountSettings.graphql.generated";
import { logError } from "@hl/shared-features/lib/services/logger";
import { Box, Space, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import Joi from "joi";

import { SupportedMediaType } from "../../../apollo/graphql.generated";
import {
  PublicUserProfileDocument,
  PublicUserProfileQuery,
  useUpdateAccountSettingsMutation,
} from "../queries/profile.graphql.generated";

import { LinkEmailInput } from "./LinkEmailInput";
import { LinkFarcasterInput } from "./LinkFarcasterInput";
import { LinkXInput } from "./LinkXInput";
import { ProfileImageInput } from "./ProfileImageInput";

interface EditProfileFormValues {
  displayName: string;
  bio: string;
  avatarUrl: string;
  website: string;
  mediaType: SupportedMediaType;
  email: string;
  rodeoUsername: string;
}

export const GeneralSettings = ({
  defaultName,
  profile,
  loadingProfile = false,
}: {
  defaultName: string;
  profile?: PublicUserProfileQuery["getPublicAccountSettings"];
  active?: boolean;
  loadingProfile?: boolean;
}) => {
  const [updateAccountSettingsMutation, { loading }] =
    useUpdateAccountSettingsMutation();

  const isLoading = loading || loadingProfile;

  const alert = useAlert();

  const updateAccountSettings = useCallback(
    async (values: EditProfileFormValues) => {
      try {
        await updateAccountSettingsMutation({
          variables: {
            input: {
              bio: values.bio || null,
              avatarUrl: values.avatarUrl || null,
              displayName:
                defaultName === values.displayName
                  ? null
                  : values.displayName || null,
              website: values.website
                ? !values.website.toLowerCase().startsWith("http")
                  ? `https://${values.website}`
                  : values.website.toLowerCase()
                : null,
              email: {
                address: values.email,
                redirectUrl: window.location.href,
              },
              rodeoUsername: values.rodeoUsername || null,
            },
          },
          refetchQueries: [
            GetUserAccountSettingsDocument,
            PublicUserProfileDocument,
          ],
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        alert.error("Error updating settings. Refresh and try again.");
        await logError({ error, message: "update account settings error" });
      }
    },
    [updateAccountSettingsMutation, alert, defaultName]
  );

  const form = useForm<EditProfileFormValues>({
    initialValues: {
      displayName: profile?.displayName || defaultName,
      bio: profile?.bio || "",
      avatarUrl: profile?.avatarUrl || "",
      mediaType: SupportedMediaType.IMAGE,
      website: profile?.website || "",
      email: profile?.socials?.email?.address || "",
      rodeoUsername: profile?.socials?.rodeo?.username || "",
    },
    validate: (values) => ({
      displayName:
        values.displayName &&
        defaultName !== values.displayName &&
        values.displayName.length < 3
          ? "Name must have more than 2 chars"
          : null,
      website:
        values.website && !validateUri(values.website)
          ? "Enter a valid website"
          : null,
      rodeoUsername:
        values.rodeoUsername && !/^[a-zA-Z0-9._-]+$/.test(values.rodeoUsername)
          ? "Enter a valid username. Alphanumeric with . _ - chars"
          : null,
    }),
  });

  useEffect(() => {
    form.setValues({
      displayName: profile?.displayName || defaultName,
      bio: profile?.bio || "",
      avatarUrl: profile?.avatarUrl || "",
      mediaType: SupportedMediaType.IMAGE,
      website: profile?.website || "",
      email: profile?.socials?.email?.address || "",
      rodeoUsername: profile?.socials?.rodeo?.username || "",
    });
  }, [profile]);

  const handleOnSubmit = useCallback(
    form.onSubmit(async (values) => {
      if (!form.isDirty()) return;
      await updateAccountSettings(values);
    }),
    [updateAccountSettings, form]
  );

  return (
    <form>
      <Stack spacing={40} maw={640}>
        <Box>
          <TextInput
            maxLength={100}
            placeholder="Add a display name"
            label="Profile name"
            disabled={
              (profile?.verified && profile?.displayName != null) || isLoading
            }
            {...form.getInputProps("displayName")}
            onBlur={handleOnSubmit}
          />
          {profile?.verified && (
            <Text size="xs" color={TEXT_COLOR.SECONDARY} mt={8}>
              Once set, your name can’t be changed unless you contact us at
              gm@highlight.xyz.
            </Text>
          )}
        </Box>
        <Box>
          <Text component="label" size="sm">
            Profile picture
          </Text>
          <Space h={6} />
          <ProfileImageInput
            profile={profile}
            avatarUrl={form.values.avatarUrl}
            avatarUrlError={(form.errors["avatarUrl"] as string) ?? ""}
            onChangeAvatarUrl={async (media) => {
              const avatarUrlValue = media?.url || "";
              form.setFieldValue("avatarUrl", media?.url || "");
              await updateAccountSettings({
                ...form.values,
                avatarUrl: avatarUrlValue,
              });
            }}
          />
        </Box>
        <TextAreaWithCounter
          placeholder="Tell people about yourself"
          label="Bio"
          minRows={3}
          {...form.getInputProps("bio")}
          autosize
          maxLength={400}
          characterCounter
          onBlur={handleOnSubmit}
        />
        <Box>
          <Text component="label" size="sm">
            Email
          </Text>
          <Space h={6} />
          <LinkEmailInput loading={isLoading} profile={profile} />
        </Box>
        <TextInput
          placeholder="https://example.com"
          label="Website"
          disabled={isLoading}
          {...form.getInputProps("website")}
          onBlur={handleOnSubmit}
        />

        <TextInput
          placeholder="username"
          label="Rodeo"
          disabled={isLoading}
          maxLength={100}
          {...form.getInputProps("rodeoUsername")}
          onBlur={handleOnSubmit}
        />

        <Box>
          <Text component="label" size="sm">
            X
          </Text>
          <Space h="xs" />
          <LinkXInput loading={isLoading} profile={profile} />
        </Box>
        <Box>
          <Text component="label" size="sm">
            Farcaster
          </Text>
          <Space h={6} />
          <LinkFarcasterInput loading={isLoading} profile={profile} />
        </Box>
      </Stack>
      <Space h="xl" />
    </form>
  );
};

const validateUri = (website: string) => {
  const websiteWithProtocol = !website.toLowerCase().startsWith("http")
    ? `https://${website}`
    : website;
  const { error } = Joi.string().uri().validate(websiteWithProtocol);
  return !error;
};
